import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BillingContext = createContext(null);

export function BillingProvider({ children }) {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setPlan(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/billing/plan");
      setPlan(data);
    } catch (e) {
      console.warn("Failed to load plan:", e?.message || e);
      setPlan(null);
    }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => {
    const isPaid = plan?.plan === "pro" || plan?.plan === "family";
    const tier = plan?.plan || "free";
    const canCreateTrip = !plan ? true : isPaid || (plan.trips_count < plan.trip_limit);
    return { plan, loading, refresh, isPaid, tier, canCreateTrip };
  }, [plan, loading, refresh]);

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling() {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error("useBilling must be used inside BillingProvider");
  return ctx;
}
